import { API_CONFIG, apiService } from '../config/api';
import { CollectionSourceId, collectionSources } from '../config/collectionSources';

export type FitbitExportFormat = 'json' | 'ndjson' | 'csv' | 'zip';
export type FitbitExportDateBasis = 'recorded_local' | 'utc';
export type ResearchColumn = { fieldId: string; name: string };
export type ResearchTemplate = { version: 1; name: string; tables: Array<{ routeKey: string; columns: ResearchColumn[] }> };
export type ResearchField = { id: string; name: string; label: string; type: string; unit: string | null; required?: boolean };
export type ResearchTable = { routeKey: string; label: string; temporalKind: string; fields: ResearchField[] };
export type SavedResearchTemplate = { id: string; template: ResearchTemplate; createdAt: string };
export type ResearchPreview = { columns: Array<{ name: string; label: string; unit: string | null; type: string }>;
  rows: Array<Record<string, unknown>>; hasMore: boolean; templateFingerprint: string };
export type FitbitExtractionProgress = {
  phase: 'queued' | 'preparing' | 'extracting' | 'saving' | 'completed'; percentage: number;
  basis: 'date-range' | 'participants'; routeKey: string | null; throughDate: string | null;
};
export type FitbitExtractionJob = {
  jobId: string; studyId: string; participantId: string; name: string; format: FitbitExportFormat;
  routeKey: string | null; routeKeys?: string[] | null; startDate: string; endDate: string; dateBasis?: FitbitExportDateBasis;
  state: 'queued' | 'running' | 'completed' | 'failed' | 'deleting'; recordCount: number; byteCount: number;
  error: string | null; createdAt: string; updatedAt: string; completedAt: string | null; fileName: string | null;
  progress?: FitbitExtractionProgress | null; attempt?: number; startedAt?: string | null;
  layout?: 'detailed' | 'research'; template?: { name: string; version: number; fingerprint: string } | null;
  deletion?: { deleteCloudFile: boolean } | null;
  trackingId?: string | null; cloudFolder?: string | null; cloudFile?: string | null; cloudFileUrl?: string | null;
  participantName?: string | null; batchId?: string | null; kind?: 'batch';
  participantCount?: number; completedCount?: number; failedCount?: number; missingCount?: number;
  participantNames?: string[]; sharedDates?: boolean; participants?: FitbitExtractionJob[];
};
export type FitbitExtractionBatch = { id: string; participants: Array<{
  participantId: string; requestId: string; startDate: string; endDate: string;
}> };
export type FitbitExtractionRequest = Pick<FitbitExtractionJob, 'participantId' | 'name' | 'format' | 'startDate' | 'endDate'> & {
  routeKeys: string[] | null; requestId: string; dateBasis?: FitbitExportDateBasis; layout?: 'detailed' | 'research'; template?: ResearchTemplate; templateFingerprint?: string;
  batch?: FitbitExtractionBatch };
export type FitbitExtractionPage = { jobs: FitbitExtractionJob[]; nextCursor: string | null };

export function createCollectionExportService(source: Exclude<CollectionSourceId, 'healthkit'> = 'fitbit') {
  const root = collectionSources[source].apiBase;
  const base = (studyId: string) => `${root}/studies/${encodeURIComponent(studyId)}/extractions`;

  const templateBase = (studyId: string) => `${root}/studies/${encodeURIComponent(studyId)}/export-templates`;

  function getResearchTemplates(studyId: string): Promise<{ catalog: ResearchTable[]; templates: SavedResearchTemplate[] }> {
    return apiService.get(templateBase(studyId));
  }

  function analyzeResearchTemplate(studyId: string, routeKey: string, text: string): Promise<{ columns: Array<ResearchColumn & { status: string; candidates: string[] }> }> {
    return apiService.post(`${templateBase(studyId)}/analyze`, { routeKey, text });
  }

  function saveResearchTemplate(studyId: string, template: ResearchTemplate): Promise<{ saved: SavedResearchTemplate }> {
    return apiService.post(templateBase(studyId), template);
  }

  function previewResearchTemplate(studyId: string, request: { participantId: string; startDate: string; endDate: string;
    routeKeys: string[] | null; template: ResearchTemplate; previewRouteKey: string; dateBasis?: FitbitExportDateBasis }): Promise<ResearchPreview> {
    return apiService.post(`${base(studyId)}/preview`, request);
  }

  function createFitbitExtraction(studyId: string, request: FitbitExtractionRequest): Promise<{ job: FitbitExtractionJob }> {
    return apiService.post(base(studyId), request);
  }

  function listFitbitExtractions(studyId: string, after?: string, filters: { search?: string; state?: string } = {}): Promise<FitbitExtractionPage> {
    const query = new URLSearchParams();
    if (after) query.set('after', after);
    if (filters.search) query.set('search', filters.search);
    if (filters.state) query.set('state', filters.state);
    return apiService.get(`${base(studyId)}${query.toString() ? `?${query}` : ''}`);
  }

  function getFitbitExtraction(studyId: string, jobId: string): Promise<{ job: FitbitExtractionJob }> {
    return apiService.get(`${base(studyId)}/${encodeURIComponent(jobId)}`);
  }

  function getFitbitExtractionDownload(studyId: string, jobId: string, packaging: 'original' | 'zip' = 'original'): Promise<{ url: string; fileName: string; expiresAt: string }> {
    return apiService.post(`${base(studyId)}/${encodeURIComponent(jobId)}/download`, { packaging });
  }

  function deleteFitbitExtraction(studyId: string, jobId: string, deleteCloudFile: boolean): Promise<{ deleted: boolean; cloudFileDeleted: boolean }> {
    return apiService.post(`${base(studyId)}/${encodeURIComponent(jobId)}/delete`, { deleteCloudFile });
  }

  function openFitbitExtractionDownload(result: { url: string; fileName: string }) {
    const apiDownload = result.url.startsWith(root + '/studies/') && /^\/api\/(?:fitbit-collection|ultrahuman)\/researcher\/studies\/[^/?]+\/extractions\/\d{13}-[a-f0-9-]{36}\/download-file\?ticket=[a-f0-9]{64}$/.test(result.url);
    const url = apiDownload ? new URL(`${API_CONFIG.BASE_URL}${result.url}`, window.location.origin) : new URL(result.url);
    const allowedApi = apiDownload && (url.protocol === 'https:' || (url.protocol === 'http:' && ['localhost', '127.0.0.1'].includes(url.hostname)));
    if ((!allowedApi && (url.protocol !== 'https:' || url.hostname !== 'storage.googleapis.com')) || url.username || url.password) {
      throw new Error('The extraction download address is invalid.');
    }
    const link = document.createElement('a');
    link.href = url.href; link.download = result.fileName; link.rel = 'noopener noreferrer'; link.referrerPolicy = 'no-referrer';
    // Keep the temporary signed URL out of the page DOM and delegated click analytics.
    link.click();
  }
  return { getResearchTemplates, analyzeResearchTemplate, saveResearchTemplate, previewResearchTemplate, createFitbitExtraction, listFitbitExtractions, getFitbitExtraction, getFitbitExtractionDownload, deleteFitbitExtraction, openFitbitExtractionDownload };
}

export const { getResearchTemplates, analyzeResearchTemplate, saveResearchTemplate, previewResearchTemplate, createFitbitExtraction, listFitbitExtractions, getFitbitExtraction, getFitbitExtractionDownload, deleteFitbitExtraction, openFitbitExtractionDownload } = createCollectionExportService();
